import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { FullWindowSpinner } from "../../shared";
import MonkeyNotFound from "../../shared/monkeyNotFound";
import icon from "../../../images/pageIcon.png";
import { Helmet } from "react-helmet";
import { UserPreferences } from "../../user-app-config/hooks/";
import HeroLayout from "../../shared/hero-layout/hero-layout";
import { getTmdbImage } from "../../../utils/consts";

import './user-profile-details.scss';
import { toast } from "react-toastify";
import UserProfileDetailsPosts from "./user-profile-details-posts/user-profile-details-posts";
import UserProfileDetailsCollections from "./user-profile-details-collections/user-profile-details-collections";
import MyProfileCollectionDetails
    from "../myProfileView/my-profile-subroutes/my-profile-lists/my-profile-collection-details/my-profile-collection-details";
import { getCachedImageUrl } from "../../../utils/utils";
import auth from "../../../utils/authentication";
import MyProfileReviews from "../myProfileView/my-profile-subroutes/my-profile-reviews/my-profile-reviews";
import { SquadDiskUsersApi } from "../../../apis/squaddisk-users/SquadDiskUsersApi";
import { SquadflixFriendsApi } from "../../../apis/squadflix-friends/SquadflixFriendsApi";
import ButtonV2 from "../../shared/button-v2/button-v2";

export type SquadDiskUserDetails = {
    uid: string;
    code: string;
    displayName: string;
    email: string;
    photoURL?: string;
    preferences?: UserPreferences;
}

const UserProfileDetails = () => {

    const { userName, code } = useParams<{ userName: string, code: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<SquadDiskUserDetails>(null);
    const location = useLocation();
    const navigate = useNavigate();

    const [friendshipStatus, setFriendshipStatus] = useState<'loading' | 'friends' | 'pending_sent' | 'pending_received' | 'not_friends'>('loading');
    const [actionLoading, setActionLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1200);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const checkFriendship = () => {
        if (!user || !auth.currentUser) return;
        setFriendshipStatus('loading');
        Promise.all([
            SquadflixFriendsApi.listFriends(),
            SquadflixFriendsApi.listFriendRequests()
        ]).then(([friendsRes, requestsRes]) => {
            if (friendsRes.success && requestsRes.success) {
                const isFriend = (friendsRes.friends ?? []).some(f => f.uid === user.uid);
                if (isFriend) {
                    setFriendshipStatus('friends');
                    return;
                }

                const isSent = (requestsRes.sent ?? []).some(f => f.uid === user.uid);
                if (isSent) {
                    setFriendshipStatus('pending_sent');
                    return;
                }

                const isReceived = (requestsRes.received ?? []).some(f => f.uid === user.uid);
                if (isReceived) {
                    setFriendshipStatus('pending_received');
                    return;
                }

                setFriendshipStatus('not_friends');
            } else {
                setFriendshipStatus('not_friends');
            }
        }).catch((err) => {
            console.error("Error checking friendship status:", err);
            setFriendshipStatus('not_friends');
        });
    };

    useEffect(() => {
        if (user) {
            checkFriendship();
        }
    }, [user]);

    const handleSendRequest = () => {
        if (!user || actionLoading) return;
        setActionLoading(true);
        SquadflixFriendsApi.sendFriendRequest({ targetUid: user.uid })
            .then((res) => {
                if (res.success) {
                    toast.success("Solicitud de amistad enviada");
                    checkFriendship();
                } else {
                    toast.error(res.message || "Error al enviar la solicitud");
                }
            })
            .catch(() => toast.error("Error al enviar la solicitud"))
            .finally(() => setActionLoading(false));
    };

    const handleCancelRequest = () => {
        if (!user || actionLoading) return;
        setActionLoading(true);
        SquadflixFriendsApi.cancelFriendRequest({ targetUid: user.uid })
            .then((res) => {
                if (res.success) {
                    toast.info("Solicitud de amistad cancelada");
                    checkFriendship();
                } else {
                    toast.error(res.message || "Error al cancelar la solicitud");
                }
            })
            .catch(() => toast.error("Error al cancelar la solicitud"))
            .finally(() => setActionLoading(false));
    };

    const handleAcceptRequest = () => {
        if (!user || actionLoading) return;
        setActionLoading(true);
        SquadflixFriendsApi.acceptFriendRequest({ targetUid: user.uid })
            .then((res) => {
                if (res.success) {
                    toast.success("¡Ahora sois amigos!");
                    checkFriendship();
                } else {
                    toast.error(res.message || "Error al aceptar la solicitud");
                }
            })
            .catch(() => toast.error("Error al aceptar la solicitud"))
            .finally(() => setActionLoading(false));
    };

    const handleRejectRequest = () => {
        if (!user || actionLoading) return;
        setActionLoading(true);
        SquadflixFriendsApi.rejectFriendRequest({ targetUid: user.uid })
            .then((res) => {
                if (res.success) {
                    toast.info("Solicitud de amistad rechazada");
                    checkFriendship();
                } else {
                    toast.error(res.message || "Error al rechazar la solicitud");
                }
            })
            .catch(() => toast.error("Error al rechazar la solicitud"))
            .finally(() => setActionLoading(false));
    };

    const handleRemoveFriend = () => {
        if (!user || actionLoading) return;
        if (!window.confirm(`¿Seguro que quieres eliminar a ${user.displayName} de tus amigos?`)) return;
        setActionLoading(true);
        SquadflixFriendsApi.removeFriend({ targetUid: user.uid })
            .then((res) => {
                if (res.success) {
                    toast.info("Amigo eliminado");
                    checkFriendship();
                } else {
                    toast.error(res.message || "Error al eliminar amigo");
                }
            })
            .catch(() => toast.error("Error al eliminar amigo"))
            .finally(() => setActionLoading(false));
    };

    const renderFriendshipButton = () => {
        if (!auth.currentUser || user?.uid === auth.currentUser?.uid) return null;

        if (friendshipStatus === 'loading') {
            return null;
        }

        if (friendshipStatus === 'friends') {
            return (
                <div className="user-profile-details__friendship-container">
                    <ButtonV2
                        variant="danger"
                        size="small"
                        onClick={handleRemoveFriend}
                        disabled={actionLoading}
                        title="Eliminar amigo"
                    >
                        <i className="fa-solid fa-user-xmark me-2"></i> Eliminar
                    </ButtonV2>
                </div>
            );
        }

        if (friendshipStatus === 'pending_sent') {
            return (
                <div className="user-profile-details__friendship-container">
                    <ButtonV2
                        variant="danger"
                        size="small"
                        onClick={handleCancelRequest}
                        disabled={actionLoading}
                    >
                        <i className="fa-solid fa-paper-plane me-2"></i>Cancelar Solicitud
                    </ButtonV2>
                </div>
            );
        }

        if (friendshipStatus === 'pending_received') {
            return (
                <div className="user-profile-details__friendship-container">
                    <ButtonV2
                        variant="primary"
                        size="small"
                        onClick={handleAcceptRequest}
                        disabled={actionLoading}
                    >
                        <i className="fa-solid fa-check me-2"></i> Aceptar
                    </ButtonV2>
                    <ButtonV2
                        variant="danger"
                        size="small"
                        onClick={handleRejectRequest}
                        disabled={actionLoading}
                    >
                        <i className="fa-solid fa-xmark me-2"></i> Rechazar
                    </ButtonV2>
                </div>
            );
        }

        // not_friends
        return (
            <div className="user-profile-details__friendship-container">
                <ButtonV2
                    variant="secondary"
                    size="small"
                    onClick={handleSendRequest}
                    disabled={actionLoading}
                >
                    <i className="fa-solid fa-user-plus me-2"></i> Enviar solicitud
                </ButtonV2>
            </div>
        );
    };

    useEffect(() => {

        setLoading(true);
        setError(null);
        setUser(null);

        SquadDiskUsersApi.findUserDetails({
            userName: userName,
            code: code
        }).then((response) => {
            setUser(response.user);
            console.log(response.user);
        }).catch((error) => {
            setError("Error fetching user details");
        }).finally(() => {
            setLoading(false);
        });
    }, [userName, code]);

    const copyUserUrl = () => {
        const userUrl = window.location.href;
        navigator.clipboard.writeText(userUrl).then(() => {
            toast.info("URL de usuario copiada al portapapeles");
        });
    };

    useEffect(() => {
        // Scroll to top when location changes
        window.scrollTo(0, 0);

        if (!user) {
            return;
        }

        if (user.uid === auth.currentUser?.uid) {
            // If the user is viewing their own profile, redirect to /my-profile
            navigate('/my-profile' + location.pathname.replace(`/user/${user.displayName}/${user.code}`, ''))
        }
    }, [location, user]);

    const subroutes = useMemo(() => {
        return [
            {
                icon: <i className="fa-solid fa-inbox"></i>,
                text: "Publicaciones",
                route: "posts",
            }, {
                icon: <i className="fa-solid fa-bookmark"></i>,
                text: "Guardados",
                route: "collections",
            }, {
                icon: <i className="fa-solid fa-star"></i>,
                text: "Reseñas",
                route: "reviews",
            }
        ]
    }, []);

    if (loading) {
        return <FullWindowSpinner />
    }

    if (error || !user) {
        return <MonkeyNotFound />
    }


    return <>
        <Helmet>
            <meta charSet="utf-8" />
            <title>SquadDisk | {user.displayName}</title>
            <link rel="icon" href={icon} />
        </Helmet>
        {isMobile && renderFriendshipButton()}
        <HeroLayout title={
            <>{user.displayName}
                <button className="user-profile-details__copy-user-url" onClick={copyUserUrl}><i
                    className="fa-solid fa-link text-muted fa-xs"></i></button>
            </>
        }
            imgStyleOverrides={{
                aspectRatio: '1 / 1',
                objectFit: 'cover',
            }}
            imgUrl={getCachedImageUrl(user.photoURL)}
            subtitle={'#' + user.code}
            options={<></>} routes={subroutes}
            topRightElement={!isMobile ? renderFriendshipButton() : undefined}
            backgroundUrl={user.preferences?.profileBackground ? getTmdbImage(user.preferences.profileBackground) : undefined}>
            <Routes>
                <Route path="posts" element={<UserProfileDetailsPosts user={user} />} />
                <Route path="collections" element={<UserProfileDetailsCollections user={user} />} />
                <Route path="collections/:id" element={<MyProfileCollectionDetails />} />
                <Route path="reviews" element={<MyProfileReviews uid={user.uid} />} />
                <Route path="*" element={<Navigate to="posts" />} />
            </Routes>
        </HeroLayout>
    </>;
}

export default UserProfileDetails;
